"use client";

import { useRef, useState } from "react";
import { ContactConfirmationDialog } from "./ContactConfirmationDialog";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "@/app/_components/security/TurnstileWidget";
import { sendFormEvent } from "@/app/ga4/eventHandler";
import { useContactForm } from "@/app/hooks/useContactForm";

type ContactFormErrors = {
  fname?: string;
  phone?: string;
  email?: string;
  comment?: string;
  turnstile?: string;
};

export default function ContactForm({ campaignId }: { campaignId?: string }) {
  const [formStarted, setFormStarted] = useState(false);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const {
    formData,
    setFormData,
    confirmationDialogOpen,
    confirmationStatus,
    isSubmitting,
    handleCorrectData,
    handleConfirmationDialogClose,
    saveLead,
  } = useContactForm();

  function validateForm() {
    const fullNameRegex = /^[a-zA-Z\s'-]+$/;
    const newErrors: ContactFormErrors = {};
    if (!formData.fname) {
      newErrors.fname = "Full name is required";
    } else if (!fullNameRegex.test(formData.fname)) {
      newErrors.fname = "Full name must only contain letters and spaces";
    } else if (formData.fname.length > 100) {
      newErrors.fname = "Full name must not exceed 100 characters";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone is required";
    } else {
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = "Invalid phone number";
      }
      if (formData.phone.length > 10) {
        newErrors.phone = "Phone number exceeds maximum length";
      }
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }
      if (formData.email.length > 50) {
        newErrors.email = "Email address exceeds maximum length";
      }
    }

    if (!turnstileToken) {
      newErrors.turnstile = "Please complete the verification challenge";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if there are no errors
  }

  function handleChange(event: any) {
    if (!formStarted) {
      sendFormEvent("CONTACT_FORM", "START");
      setFormStarted(true);
    }
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }

  async function handleSubmit(event: React.SubmitEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!validateForm()) {
      if (process.env.NODE_ENV !== "development") {
        sendFormEvent("CONTACT_FORM", "INVALID/ERROR");
      }
      return;
    }

    const didSaveLead = await saveLead(campaignId, turnstileToken);

    if (didSaveLead) {
      setTurnstileToken("");
      turnstileRef.current?.reset();
    } else {
      turnstileRef.current?.reset();
    }
  }

  return (
    <>
      <form className="demo-form contact" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-auxiliary-field">
            <label htmlFor="company">Company</label>
            <input
              type="text"
              id="company"
              name="company"
              tabIndex={-1}
              autoComplete="new-password"
              onChange={handleChange}
              value={formData.company}
            />
          </div>
          <div className="input-group">
            <label>
              Full Name
              {errors.fname && <label className="error">{errors.fname}</label>}
            </label>
            <input
              type="text"
              id="fname"
              name="fname"
              placeholder="Full Name"
              onChange={handleChange}
              value={formData.fname}
            />
          </div>
          <div className="input-group">
            <label>
              Email
              {errors.email && <label className="error">{errors.email}</label>}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="your@email.com"
              onChange={handleChange}
              value={formData.email}
            />
          </div>
          <div className="input-group">
            <label>
              Phone
              {errors.phone && <label className="error">{errors.phone}</label>}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="123-456-7890"
              onChange={handleChange}
              value={formData.phone}
            />
          </div>
          <div className="input-group">
            <label>
              {errors.comment && (
                <label className="error">{errors.comment}</label>
              )}
            </label>
            <textarea
              id="comment"
              name="comment"
              onChange={handleChange}
              value={formData.comment}
              placeholder="How can we help you?"
            ></textarea>
          </div>
          <div className="button-wrapper"></div>
          <div className="input-group">
            {errors.turnstile && (
              <label className="error">{errors.turnstile}</label>
            )}
            <TurnstileWidget
              action="contact"
              ref={turnstileRef}
              onTokenChange={(token) => {
                setTurnstileToken(token);
                setErrors((currentErrors) => {
                  const { turnstile, ...rest } = currentErrors;

                  return rest;
                });
              }}
              onTokenClear={() => {
                setTurnstileToken("");
              }}
            />
          </div>
          <button className="form-submit" disabled={isSubmitting} type="submit">
            {isSubmitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </form>

      {confirmationStatus && (
        <ContactConfirmationDialog
          open={confirmationDialogOpen}
          status={confirmationStatus}
          contactEmail="customerservice@rpscorporation.com"
          contactPhone="+1 855-965-0011"
          formData={formData}
          onCorrectData={handleCorrectData}
          onOpenChange={handleConfirmationDialogClose}
        />
      )}
    </>
  );
}
